import React, { useEffect, useRef } from 'react';
import { useValueRef } from '@/hooks/useValueRef';
import { RecaptchaProps } from '../types/recatptcha-props';
import { useRecaptchaSize } from '../hooks/useRecaptchaSize';
import { RecaptchaError } from '../Error';
import { RecaptchaRoot } from '../Root';
import { RecaptchaWrapper } from '../Wrapper';

declare global {
  interface Window {
    onRecaptchaLoadedCB: () => void;
    grecaptcha?: {
      enterprise?: {
        ready: (callback: () => void) => void;
        reset: () => void;
        render: (
          container: any,
          options: {
            sitekey: string;
            badge?: 'bottomright' | 'bottomleft' | 'inline';
            action?: string;
            callback?: (token: string) => void;
            'error-callback'?: (token: string) => void;
          },
        ) => void;
      };
    };
  }
}

const SCRIPT_ID = 'recaptcha-v3-script';

const SCRIPT_SRC = 'https://www.recaptcha.net/recaptcha/enterprise.js?onload=onRecaptchaLoadedCB&render=explicit&hl=en';

const RecaptchaV3: React.FC<RecaptchaProps> = ({ captchaKey, timestamp, error, errorText, onChange }) => {
  const wrapperRef = useRef<HTMLDivElement>(null);

  const onChangeRef = useValueRef(onChange);
  const containerRef = useRef<HTMLDivElement>(null);
  const renderedRef = useRef(false);

  const { width, height, scale } = useRecaptchaSize(wrapperRef);

  useEffect(() => {
    if (!captchaKey || renderedRef.current) {
      return;
    }

    const executeCaptcha = () => {
      try {
        window.grecaptcha?.enterprise?.ready(() => {
          try {
            if (renderedRef.current) {
              return;
            }
            window.grecaptcha?.enterprise?.render(containerRef.current, {
              sitekey: captchaKey,
              badge: 'inline',
              callback: (token) => {
                onChangeRef.current(token);
              },
              'error-callback': (error) => {
                console.log('[RecaptchaV3] error;\n', error);
              },
            });
            renderedRef.current = true;
          } catch (e) {}
        });
      } catch (e) {}
    };

    const existingScript = document.getElementById(SCRIPT_ID) as HTMLScriptElement | null;

    if (existingScript) {
      executeCaptcha();
      return;
    }

    window.onRecaptchaLoadedCB = executeCaptcha;

    const script = document.createElement('script');
    script.id = SCRIPT_ID;
    script.src = SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.onload = executeCaptcha;
    document.head.appendChild(script);
  }, [captchaKey]);

  useEffect(() => {
    try {
      window.grecaptcha?.enterprise?.reset();
    } catch (e) {}
  }, [captchaKey, timestamp]);

  return (
    <RecaptchaRoot ref={wrapperRef}>
      <RecaptchaWrapper width={width} height={height}>
        <div ref={containerRef} style={{ transform: `scale(${scale})` }}></div>
      </RecaptchaWrapper>
      <RecaptchaError error={error} errorText={errorText} />
    </RecaptchaRoot>
  );
};

export default React.memo(RecaptchaV3);
